import { CheckCircle2, Clock, HelpCircle, XCircle, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { getLeads } from '@/modules/admin/api/leads.api'
import { Badge } from '@/shared/components/ui/badge'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/shared/components/ui/card'
import { cn } from '@/shared/lib/utils'

const STATUS_CONFIG = {
  won: {
    label: 'Won',
    badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    icon: CheckCircle2,
  },
  lost: {
    label: 'Lost',
    badgeClass: 'border-red-200 bg-red-50 text-red-700',
    icon: XCircle,
  },
  in_progress: {
    label: 'In Progress',
    badgeClass: 'border-amber-200 bg-amber-50 text-amber-700',
    icon: Clock,
  },
  open: {
    label: 'Open',
    badgeClass: 'border-blue-200 bg-blue-50 text-blue-700',
    icon: HelpCircle,
  },
}

function SalespersonTable({ person, period, customRange }) {
  const [leads, setLeads] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true
    const fetchLeads = async () => {
      setIsLoading(true)
      try {
        const res = await getLeads({
          salesPerson: person._id || person.salesId,
          status: 'won',
          limit: 100,
          period: period || 'all',
          startDate: period === 'custom' ? customRange?.startDate : undefined,
          endDate: period === 'custom' ? customRange?.endDate : undefined,
        })
        if (isMounted) {
          setLeads(res?.data || [])
        }
      } catch (err) {
        console.error('Failed to load leads for', person.name, err)
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }
    fetchLeads()
    return () => {
      isMounted = false
    }
  }, [person, period, customRange])

  const dynamicTotalValue = leads.reduce((sum, lead) => {
    const leadTotal =
      lead.products?.reduce(
        (s, p) => s + (Number(p.quantity) || 0) * (Number(p.value) || 0),
        0,
      ) || 0
    return sum + leadTotal
  }, 0)

  return (
    <Card className="mb-4 overflow-hidden rounded-2xl border border-black/[0.06] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
      <CardHeader className="border-b border-black/[0.05] bg-slate-50/50 px-4 py-4 sm:px-5">
        <div className="flex justify-between items-center">
          <CardTitle className="text-sm font-bold text-slate-900">
            {person.name}
          </CardTitle>
          <div className="text-sm font-extrabold text-emerald-600">
            {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              maximumFractionDigits: 0,
            }).format(dynamicTotalValue)}
          </div>
        </div>
        <CardDescription className="text-xs text-slate-500 mt-1">
          Won leads (showing up to 100)
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : leads.length === 0 ? (
          <div className="py-8 text-center text-xs text-slate-400">
            No leads assigned yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Value</th>
                  <th className="px-4 py-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leads.map((lead) => {
                  const statusConf =
                    STATUS_CONFIG[lead.status] || STATUS_CONFIG.open
                  const StatusIcon = statusConf.icon

                  const products = lead.products || []
                  const totalValue = products.reduce(
                    (sum, p) =>
                      sum + (Number(p.quantity) || 0) * (Number(p.value) || 0),
                    0,
                  )
                  const displayValue = new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    maximumFractionDigits: 0,
                  }).format(totalValue)

                  return (
                    <tr
                      key={lead._id}
                      className="transition hover:bg-slate-50/50"
                    >
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {lead.name || '-'}
                      </td>
                      <td className="px-4 py-3 text-xs capitalize text-slate-600">
                        {lead.source || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-700">
                        {totalValue > 0 ? displayValue : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Badge
                          className={cn(
                            'gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                            statusConf.badgeClass,
                          )}
                        >
                          <StatusIcon className="size-3" />
                          {statusConf.label}
                        </Badge>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default function SalespersonTablesSection({
  data = [],
  period,
  customRange,
}) {
  const list = Array.isArray(data) ? data : []

  if (list.length === 0) return null

  return (
    <div className="mt-6 flex flex-col gap-4">
      <h2 className="text-lg font-bold text-slate-900 px-1">
        Salesperson Leads Breakdown
      </h2>
      {list.map((person) => (
        <SalespersonTable
          key={person.name}
          person={person}
          period={period}
          customRange={customRange}
        />
      ))}
    </div>
  )
}
